import { useMemo, useState } from 'react';
import { members as Api, ApiError, type Member, type MemberInput, type MemberStatus } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { Modal } from '../components/Modal';
import { MemberForm } from './MemberForm';
import { EnrolForm } from './EnrolForm';
import { parseStamp, stampDay } from '../lib/time';

const FILTERS = ['all', 'calls', 'news', 'learning', 'none', 'expired'] as const;

const FILTER_LABEL: Record<string, string> = {
  all: 'all', calls: 'trade calls', news: 'news', learning: 'learning',
  none: 'no access', expired: 'expired',
};

/** What somebody holds, in one line. */
const heldLine = (m: Member) => (m.access?.length
  ? m.access.map((a) => a.label).join(', ')
  : '—');
type Filter = (typeof FILTERS)[number];

const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const day = stampDay;

const STATUS_STYLE: Record<MemberStatus, string> = {
  active: 'rgba(62,207,142,.14);color:var(--up)',
  expired: 'rgba(var(--wash), calc(.06 * var(--washx)));color:var(--tx2)',
  payment_failed: 'rgba(240,100,91,.13);color:var(--down)',
  blocked: 'rgba(240,100,91,.13);color:var(--down)',
};

/** "blocked" is the stored value; "suspended" is what it means to a person. */
const statusLabel = (s: MemberStatus) => (s === 'blocked' ? 'suspended' : s.replace('_', ' '));

export function Members() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<Member | 'new' | null>(null);
  const [enrolling, setEnrolling] = useState<Member | null>(null);
  const [notice, setNotice] = useState('');
  const [switching, setSwitching] = useState<number | null>(null);

  /* The tab is opened before the request so the browser counts it as the
     click's own; the link is filled in when the server answers. */
  async function switchTo(m: Member) {
    const tab = window.open('', '_blank');
    setSwitching(m.id);
    try {
      const { url } = await Api.switch(m.id);
      if (tab) tab.location.href = url; else window.location.href = url;
    } catch (e) {
      tab?.close();
      setNotice(`Could not switch — ${(e as Error).message}`);
    } finally { setSwitching(null); }
  }

  const rows = useMemo(() => {
    const all = [...(data?.members ?? [])].sort((a, b) => {
      const ta = parseStamp(a.created_at)?.getTime() || 0;
      const tb = parseStamp(b.created_at)?.getTime() || 0;
      if (tb !== ta) return tb - ta;
      return b.id - a.id;
    });
    if (filter === 'all') return all;
    if (filter === 'expired') return all.filter((m) => m.status === 'expired');
    if (filter === 'none') return all.filter((m) => !m.access?.length);
    return all.filter((m) => (m.access ?? []).some((a) => a.kind === 'module' && a.ref === filter));
  }, [data, filter]);

  const sel = useSelection(rows);

  async function save(f: MemberInput) {
    if (editing === 'new') await Api.create(f);
    else if (editing) await Api.update(editing.id, f);
    await reload();
  }

  function exportCsv() {
    const head = ['S.N.', 'Name', 'Email', 'Mobile', 'Access', 'Status', 'Joined', 'Runs until', 'Lifetime value'];
    const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const total = rows.length;
    const csv = [head, ...rows.map((m, idx) => [
      total - idx, m.name, m.email, m.mobile, heldLine(m), m.status, m.created_at,
      (m.access ?? []).map((a) => a.expires_at).filter(Boolean).sort()[0] ?? '', m.lifetime_value,
    ])].map((r) => r.map(cell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'tradevix-members.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const stats = data?.stats;
  /* The device limit in Settings, read off any member who follows it — so
     the edit form can say what "default" means without a settings permission. */
  const defaultLimit = data?.members.find((m) => m.device_limit == null)?.device_limit_effective ?? null;

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Members &amp; subscriptions</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : stats ? `${stats.total} account${stats.total === 1 ? '' : 's'} · ${stats.paying} paying · ${stats.renewals} renewing this week`
              : '—'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
          <button className="b bo" type="button" onClick={exportCsv} disabled={!data?.members.length}>Export CSV</button>
          <button className="b bg" type="button" onClick={() => setEditing('new')}>Add member</button>
        </div>
      </div>

      {error ? (
        <div className="err">
          {error instanceof ApiError && error.isForbidden
            ? 'Your role cannot see members.'
            : `Could not load members — ${error.message}`}
          <button className="b bo" type="button" style={{ marginLeft: 12 }} onClick={() => void reload()}>Try again</button>
        </div>
      ) : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div className="sg">
            {FILTERS.map((f) => (
              <span key={f} {...(filter === f ? { 'data-on': '' } : {})} onClick={() => { setFilter(f); sel.clear(); }}>
                {FILTER_LABEL[f] ?? f}
              </span>
            ))}
          </div>

        </div>

        <BulkActions
          path="/members" noun="account"
          rows={sel.picked.map((m) => ({ id: m.id, label: `${m.name || '—'} · ${m.email}` }))}
          actions={[['activate', 'Activate'], ['expire', 'Expire'], ['suspend', 'Suspend']]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} />
                <th style={{ width: 50, textAlign: 'center' }}>#</th>
                <th>Member</th><th>Access</th><th>Status</th><th>Joined</th>
                <th>Runs until</th><th>Lifetime</th><th />
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={9} tick />
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="muted" style={{ padding: '26px 8px' }}>
                  {data?.members.length ? 'No members match this filter.'
                    : 'No members yet. People who register on the site appear here.'}
                </td></tr>
              ) : rows.map((m, idx) => (
                <tr key={m.id} style={sel.isOn(m.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(m.id)} label={m.name || m.email}
                    onChange={(v) => sel.toggle(m.id, v)} />
                  {/* Newest first, so the number does not shift under someone
                      when a member signs up mid-scroll. */}
                  <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--tx3)', fontSize: 12 }}>
                    {rows.length - idx}
                  </td>
                  <td>
                    <div style={{ fontWeight: 700 }}>{m.name || '—'}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>
                      {[m.mobile, m.email].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td style={{ color: 'var(--tx2)', fontSize: 12.5 }}>{heldLine(m)}</td>
                  <td>
                    <span className="tg" style={{ background: STATUS_STYLE[m.status]?.split(';')[0] }}>
                      {statusLabel(m.status)}
                    </span>
                    {!m.has_password && (
                      <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 4 }}>no password</div>
                    )}
                    {m.devices_in_use || m.two_factor ? (
                      <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 4 }}
                        title="Devices signed in, of the number allowed">
                        {[
                          m.devices_in_use ? (m.device_limit_effective
                            ? `${m.devices_in_use} of ${m.device_limit_effective} device${m.device_limit_effective === 1 ? '' : 's'}`
                            : `${m.devices_in_use} device${m.devices_in_use === 1 ? '' : 's'}`) : '',
                          m.two_factor ? 'two-step' : '',
                        ].filter(Boolean).join(' · ')}
                      </div>
                    ) : null}
                  </td>
                  <td className="muted">{day(m.created_at)}</td>
                  <td className="muted">
                    {day((m.access ?? []).map((a) => a.expires_at).filter(Boolean).sort()[0] ?? null)}
                  </td>
                  <td>{money(m.lifetime_value)}</td>
                  <td className="right">
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" disabled={switching === m.id} onClick={() => void switchTo(m)}
                      title="Open the member area as this member (read-only)">
                      {switching === m.id ? 'Opening…' : 'Switch'}
                    </button>{' '}
                    <button className="b bg" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setEnrolling(m)}
                      title="Add to a pass, course or batch">Add</button>{' '}
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setEditing(m)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {notice ? (
        <div className={notice.startsWith('Could not') ? 'err' : 'crd'} style={{ marginTop: 12, padding: '12px 16px', cursor: 'pointer' }}
          onClick={() => setNotice('')} title="Dismiss">{notice}</div>
      ) : null}

      {enrolling ? (
        <EnrolForm
          member={enrolling}
          onCancel={() => setEnrolling(null)}
          onDone={(msg) => { setEnrolling(null); setNotice(msg); void reload(); }}
        />
      ) : null}

      {editing ? (
        <MemberForm
          member={editing === 'new' ? null : editing}
          defaultLimit={defaultLimit}
          onChanged={() => void reload()}
          onCancel={() => setEditing(null)}
          onSave={save}
          onDone={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

export { Modal };
